import {memo} from 'react';
import type {AnchorHTMLAttributes, PropsWithChildren} from 'react';
import {Link as RouterLink} from 'react-router';

import {useShouldBeClientRouted} from './hooks.js';

export type LinkProps = PropsWithChildren<
  {
    href: string;
    /**
     * Forces a full-page navigation regardless of whether the path is a
     * React route.
     *
     * You should rarely need to set this — `<Link>` checks both the
     * ReactPages registry and React Router route definitions and falls
     * back to a hard link automatically.
     */
    hardLink?: boolean;
  } & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>
>;

export const Link = memo(function Link({
  href,
  hardLink = false,
  children,
  ...anchorProps
}: LinkProps) {
  const useClientRouting = useShouldBeClientRouted(href, {hardLink});

  if (useClientRouting) {
    return (
      <RouterLink to={href} {...anchorProps}>
        {children}
      </RouterLink>
    );
  }

  return (
    <a href={href} {...anchorProps}>
      {children}
    </a>
  );
});
